import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowDown, ArrowUp, Loader2 } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from '@/components/utils/translations';
import { appendAnalyticsWindow, coerceAnalyticsWindow } from '@/lib/analyticsWindow';
import { useUser } from '@/contexts/UserContext';
import MessageKeywordsWordCloud, { parseKeywordsPayload } from '@/components/analytics/MessageKeywordsWordCloud';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

const API_BASE_URL =
  window.location.hostname === 'localhost' ? 'https://api.neogtm.com' : 'https://api.neogtm.com';

/** Ellipsis threshold for link display names (full URL in tooltip). */
const LINK_DISPLAY_NAME_TRUNC = 60;
const SIGNAL_COLLAPSE = 10;

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** Minimum sends before day/hour rates are plotted or peak-highlighted (avoids noisy weekend/hour spikes). */
const MIN_SENDS_FOR_RATE = 10;

/** Curated IANA options (label shown as in product spec). */
const SEND_TIMING_TZ_OPTIONS = [
  { value: 'America/New_York', label: 'America/New_York (Eastern — US)' },
  { value: 'America/Chicago', label: 'America/Chicago (Central — US)' },
  { value: 'America/Denver', label: 'America/Denver (Mountain — US)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (Pacific — US/Canada)' },
  { value: 'Europe/London', label: 'Europe/London (UK)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (Central Europe)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (Singapore / Malaysia)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (Japan)' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (Hong Kong)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (Eastern Australia)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (New Zealand)' },
  { value: 'UTC', label: 'UTC' },
];

function bestRatesAmong(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return {
    bestOpen: Math.max(0, ...list.map((r) => Number(r?.open_rate || 0))),
    bestClick: Math.max(0, ...list.map((r) => Number(r?.click_rate || 0))),
    bestReply: Math.max(0, ...list.map((r) => Number(r?.reply_rate || 0))),
  };
}

function prettifySnakeKey(k) {
  const raw = String(k || '').trim();
  if (!raw) return '—';
  return raw
    .split('_')
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

/** BE `signal_family` values with curated display labels + tooltips (`strategic_chunk`/`strategic_doc` are merged to `account_insights` before display). */
const KNOWN_SIGNAL_FAMILIES = new Set([
  'strategic_priority',
  'account_insights',
  'momentum',
  'activity',
  'mandatory_signal',
  'framework_based',
  'trend_based',
  'deal',
  'case_study',
  'default',
]);

/** Merge `strategic_chunk` + `strategic_doc` into one "Account Insights" row; recompute rates from summed counts. */
function mergeSignalFamilyRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const out = [];
  const merged = { total_sent: 0, opened: 0, clicked: 0, replied: 0 };
  for (const r of list) {
    const fam = String(r?.signal_family ?? r?.key ?? '').trim();
    if (fam === 'strategic_chunk' || fam === 'strategic_doc') {
      merged.total_sent += Number(r.total_sent) || 0;
      merged.opened += Number(r.opened) || 0;
      merged.clicked += Number(r.clicked) || 0;
      merged.replied += Number(r.replied) || 0;
    } else {
      out.push({ ...r });
    }
  }
  const rate = (n, d) => {
    const den = Number(d) || 0;
    const num = Number(n) || 0;
    if (den <= 0) return 0;
    return Math.round((num / den) * 100 * 100) / 100;
  };
  if (merged.total_sent > 0) {
    const ts = merged.total_sent;
    out.push({
      signal_family: 'account_insights',
      key: 'account_insights',
      ...merged,
      open_rate: rate(merged.opened, ts),
      click_rate: rate(merged.clicked, ts),
      reply_rate: rate(merged.replied, ts),
    });
  }
  out.sort((a, b) => (Number(b?.total_sent) || 0) - (Number(a?.total_sent) || 0));
  return out;
}

function signalFamilyDisplayLabel(key, t) {
  const k = String(key || '').trim().toLowerCase();
  if (!k) return '—';
  if (k === 'strategic_chunk' || k === 'strategic_doc') return t('signalFamilyLabel_account_insights');
  if (KNOWN_SIGNAL_FAMILIES.has(k)) return t(`signalFamilyLabel_${k}`);
  return prettifySnakeKey(k);
}

function signalFamilyTooltip(key, t) {
  const k = String(key || '').trim().toLowerCase();
  if (k === 'strategic_chunk' || k === 'strategic_doc') return t('signalFamilyHint_account_insights');
  if (KNOWN_SIGNAL_FAMILIES.has(k)) return t(`signalFamilyHint_${k}`);
  return undefined;
}

function ctaFamilyDisplayLabel(key, t) {
  const k = String(key || '').trim();
  const idByKey = {
    soft_binary: 'ctaFamilyLabel_soft_binary',
    action_oriented: 'ctaFamilyLabel_action_oriented',
    other: 'ctaFamilyLabel_other',
    no_cta: 'ctaFamilyLabel_no_cta',
  };
  const tid = idByKey[k];
  if (tid) return t(tid);
  return prettifySnakeKey(k);
}

function formatRate(rate, sends) {
  const s = Number(sends || 0);
  const r = Number(rate);
  if (!Number.isFinite(r) || s <= 0) return '—';
  return `${r.toFixed(1)}%`;
}

function pickFirstFiniteNumber(row, keys) {
  for (const k of keys) {
    const n = Number(row?.[k]);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** BE may return click_rate as 0–100 (breakdown rows) or 0–1 (fraction). */
function normalizeClickRatePercent(rawRate) {
  const r = Number(rawRate);
  if (!Number.isFinite(r)) return null;
  if (r > 0 && r <= 1) return Math.round(r * 10000) / 100;
  return Math.round(r * 100) / 100;
}

function formatLinkClickRate(rate) {
  const r = normalizeClickRatePercent(rate);
  if (r == null) return '—';
  return `${r.toFixed(1)}%`;
}

/** Combine playbook + ad-hoc send buckets: sum counts, recompute rates (by_send_day / by_send_hour shape). */
function combineSendBuckets(rowsA, rowsB, keyField) {
  const acc = new Map();
  for (const r of [...(rowsA || []), ...(rowsB || [])]) {
    const k = String(r?.[keyField] ?? r?.key ?? '').trim();
    if (!k) continue;
    const cur = acc.get(k) || {
      [keyField]: r[keyField] ?? r.key,
      key: k,
      hour_label: r.hour_label,
      total_sent: 0,
      opened: 0,
      clicked: 0,
      replied: 0,
    };
    cur.total_sent += Number(r.total_sent) || 0;
    cur.opened += Number(r.opened) || 0;
    cur.clicked += Number(r.clicked) || 0;
    cur.replied += Number(r.replied) || 0;
    acc.set(k, cur);
  }
  const rate = (n, d) => (Number(d) > 0 ? Math.round((Number(n) / Number(d)) * 10000) / 100 : 0);
  return Array.from(acc.values()).map((c) => ({
    ...c,
    open_rate: rate(c.opened, c.total_sent),
    click_rate: rate(c.clicked, c.total_sent),
    reply_rate: rate(c.replied, c.total_sent),
  }));
}

function fillDays(rows) {
  const byKey = new Map((rows || []).map((r) => [String(r.day_of_week ?? r.key ?? '').trim(), r]));
  return DAY_ORDER.map((d) => {
    const row = byKey.get(d);
    if (row) return { ...row, dayShort: d.slice(0, 3) };
    return {
      day_of_week: d,
      dayShort: d.slice(0, 3),
      key: d,
      total_sent: 0,
      opened: 0,
      clicked: 0,
      replied: 0,
      open_rate: 0,
      click_rate: 0,
      reply_rate: 0,
    };
  });
}

function fillHours(rows) {
  const byKey = new Map();
  for (const r of rows || []) {
    const h = Number(r.hour ?? r.key);
    if (Number.isFinite(h) && h >= 0 && h <= 23) byKey.set(h, r);
  }
  return Array.from({ length: 24 }, (_, h) => {
    const row = byKey.get(h);
    if (row) return { ...row, hourKey: h };
    return {
      hour: h,
      hourKey: h,
      hour_label: `${String(h).padStart(2, '0')}:00`,
      key: String(h),
      total_sent: 0,
      opened: 0,
      clicked: 0,
      replied: 0,
      open_rate: 0,
      click_rate: 0,
      reply_rate: 0,
    };
  });
}

/** Index of peak reply_rate among rows with total_sent >= minSends; else -1. */
function pickPeakIndex(rows, minSends = MIN_SENDS_FOR_RATE) {
  let bestIdx = -1;
  let bestReply = -1;
  (rows || []).forEach((r, i) => {
    if ((Number(r.total_sent) || 0) < minSends) return;
    const rr = Number(r.reply_rate) || 0;
    if (rr > bestReply) {
      bestReply = rr;
      bestIdx = i;
    }
  });
  return bestIdx;
}

function fmtPctOneDec(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0.0';
  return v.toFixed(1);
}

function linkDisplayName(rawUrl) {
  try {
    const u = new URL(rawUrl);
    if (
      /(^|\.)youtube\.com$/i.test(u.hostname) &&
      (u.pathname === '/watch' || u.pathname.startsWith('/watch'))
    ) {
      return 'YouTube Video';
    }
    const last = u.pathname.split('/').filter(Boolean).pop() || u.hostname;
    const stripped = last
      .replace(/\.(pdf|html?|aspx?|php)$/i, '')
      .replace(/^\w+_/, '')
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const titled = stripped
      .split(' ')
      .map((w) => (w.length <= 3 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1).toLowerCase()))
      .join(' ');
    return titled || u.hostname;
  } catch {
    return rawUrl;
  }
}

function truncateLinkDisplayName(rawName) {
  const s = String(rawName ?? '').trim();
  if (!s) return { display: '—', truncated: false };
  if (s.length <= LINK_DISPLAY_NAME_TRUNC) return { display: s, truncated: false };
  return { display: `${s.slice(0, LINK_DISPLAY_NAME_TRUNC - 1)}…`, truncated: true };
}

/** BE may expose link-click breakdown under one of several keys — use the first array present. */
function pickLinkClickedRows(data) {
  if (!data || typeof data !== 'object') return [];
  const keys = ['by_link_clicked', 'by_clicked_link', 'by_link', 'by_urls'];
  for (const k of keys) {
    const arr = data[k];
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

const LINK_ROW_SEND_KEYS = [
  'total_sent',
  'totalSent',
  'email_sends',
  'emailSends',
  'messages_with_link',
  'messagesWithLink',
  'unique_messages_with_link',
  'uniqueMessagesWithLink',
  'unique_messages_sent',
  'uniqueMessagesSent',
  'messages_sent',
  'messagesSent',
  'message_count',
  'messageCount',
  'sent_count',
  'sentCount',
  /** Messages that included this URL (denominator when distinct from clicked count). */
  'unique_messages',
  'uniqueMessages',
];

const LINK_ROW_CLICKED_KEYS = [
  'unique_messages_clicked',
  'uniqueMessagesClicked',
  'clicked',
  'human_clicks',
  'humanClicks',
  'total_human_clicks',
  'totalHumanClicks',
];

const LINK_ROW_RATE_KEYS = [
  'click_rate',
  'clickRate',
  'true_click_rate',
  'trueClickRate',
];

/**
 * Scanner-filtered click rate for a link row.
 * BE may send `click_rate` + `total_sent`, or counts only (`unique_messages_clicked` / sends-with-link).
 */
function linkRowTrueClickRate(row, sourceTotalSent = 0) {
  const pre = pickFirstFiniteNumber(row, LINK_ROW_RATE_KEYS);
  if (pre != null) {
    const sends = pickFirstFiniteNumber(row, LINK_ROW_SEND_KEYS) ?? 0;
    return { rate: normalizeClickRatePercent(pre), sends };
  }

  const clicked = pickFirstFiniteNumber(row, LINK_ROW_CLICKED_KEYS) ?? 0;
  let sends = pickFirstFiniteNumber(row, LINK_ROW_SEND_KEYS);

  // `unique_messages` = messages containing the link; must exceed or equal clicked count to be a denominator.
  const withLink = pickFirstFiniteNumber(row, ['unique_messages', 'uniqueMessages']);
  if (
    (sends == null || sends <= 0) &&
    withLink != null &&
    withLink > 0 &&
    withLink >= clicked
  ) {
    sends = withLink;
  }

  if ((sends == null || sends <= 0) && sourceTotalSent > 0) {
    sends = sourceTotalSent;
  }

  if (sends != null && sends > 0) {
    return {
      rate: Math.round((clicked / sends) * 10000) / 100,
      sends,
    };
  }

  return { rate: null, sends: 0 };
}

/** Tie-break when click rates match (or are missing): prefer more clicked messages, then raw clicks. */
function linkRowEngagementScore(row) {
  return (
    pickFirstFiniteNumber(row, LINK_ROW_CLICKED_KEYS) ??
    pickFirstFiniteNumber(row, ['total_clicks', 'totalClicks']) ??
    0
  );
}

function compareLinkClickRateEntries(a, b, direction) {
  const ra = Number.isFinite(a.rate) ? a.rate : null;
  const rb = Number.isFinite(b.rate) ? b.rate : null;
  const aNull = ra == null;
  const bNull = rb == null;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  const mul = direction === 'asc' ? 1 : -1;
  if (ra < rb) return -1 * mul;
  if (ra > rb) return 1 * mul;
  const engageDiff = linkRowEngagementScore(b.row) - linkRowEngagementScore(a.row);
  if (engageDiff !== 0) return engageDiff * mul;
  return ((b.sends || 0) - (a.sends || 0)) * mul;
}

function mapLinkClickedEntries(rows, sourceTotalSent = 0) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    row,
    ...linkRowTrueClickRate(row, sourceTotalSent),
  }));
}

function LinksByTouchPanel({ title, cohort, touches }) {
  const cohortTouches = (Array.isArray(touches) ? touches : [])
    .filter((t) => t.cohort === cohort)
    .sort((a, b) => a.touch - b.touch);

  if (cohortTouches.length === 0) {
    return (
      <div className="text-xs text-gray-500 italic">
        Not enough data yet ({title.toLowerCase()}).
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-xs uppercase tracking-wide text-gray-500">
        Best-performing links by touch — {title}
      </div>
      {cohortTouches.map((bucket) => (
        <div key={`${bucket.cohort}-${bucket.touch}`}>
          <div className="text-xs font-semibold text-[#0A1A2F] mb-1">
            Touch {bucket.touch}
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 text-[10px] uppercase">
                <th className="text-left py-1">Asset</th>
                <th className="text-right py-1">Sent</th>
                <th className="text-right py-1">Clicks</th>
                <th className="text-right py-1">True click rate</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(bucket.links) ? bucket.links : []).slice(0, 6).map((l) => {
                const clickRate = Number(l.click_rate);
                const rateLabel = Number.isFinite(clickRate) ? `${clickRate.toFixed(1)}%` : '—';
                return (
                  <tr key={l.url} className="border-t border-gray-100">
                    <td className="py-1 pr-2 truncate max-w-[200px]" title={l.url}>
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00A3A3] hover:underline"
                      >
                        {l.label}
                      </a>
                    </td>
                    <td className="py-1 text-right text-gray-600 tabular-nums">{l.sent}</td>
                    <td className="py-1 text-right text-gray-600 tabular-nums">{l.clicks}</td>
                    <td
                      className={`py-1 text-right font-semibold tabular-nums ${
                        Number.isFinite(clickRate) && clickRate >= 8
                          ? 'text-emerald-700 bg-emerald-50 rounded-sm px-1'
                          : ''
                      }`}
                    >
                      {rateLabel}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function LinkClickedBreakdownTable({
  title,
  rows,
  sourceTotalSent = 0,
  expanded,
  onToggleExpand,
  t,
}) {
  const [rateSortDir, setRateSortDir] = useState('desc');

  const sorted = useMemo(() => {
    const entries = mapLinkClickedEntries(rows, sourceTotalSent);
    return [...entries].sort((a, b) => compareLinkClickRateEntries(a, b, rateSortDir));
  }, [rows, sourceTotalSent, rateSortDir]);

  if (sorted.length === 0) return null;
  const showCollapse = sorted.length > SIGNAL_COLLAPSE;
  const displayed = showCollapse && !expanded ? sorted.slice(0, SIGNAL_COLLAPSE) : sorted;
  const bestRate = Math.max(
    0,
    ...displayed.map((e) => (Number.isFinite(e.rate) ? e.rate : 0)),
  );

  return (
    <div className="mb-5 last:mb-0 min-w-0 overflow-x-hidden">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</h4>
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[70%] min-w-0">{t('messagePerfLabelLinkUrl')}</TableHead>
            <TableHead className="text-right w-[30%] whitespace-nowrap p-0">
              <button
                type="button"
                onClick={() => setRateSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
                className="inline-flex items-center justify-end gap-1 w-full px-4 py-3 font-medium text-gray-700 hover:text-[#00A3A3] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2] rounded-sm"
                aria-label={
                  rateSortDir === 'desc'
                    ? 'True click rate sorted high to low. Click to sort low to high.'
                    : 'True click rate sorted low to high. Click to sort high to low.'
                }
              >
                {t('messagePerfLinkTrueClickRate')}
                {rateSortDir === 'desc' ? (
                  <ArrowDown className="w-3.5 h-3.5 shrink-0" aria-hidden />
                ) : (
                  <ArrowUp className="w-3.5 h-3.5 shrink-0" aria-hidden />
                )}
              </button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayed.map(({ row, rate, sends }, idx) => {
            const href = String(row?.url ?? row?.link ?? '').trim();
            const readable = href.length > 0 ? linkDisplayName(href) : '';
            const { display } = truncateLinkDisplayName(readable);
            const key = `${href || 'row'}-${idx}`;
            const rateNum = Number.isFinite(rate) ? rate : null;
            const isBest = rateNum != null && rateNum > 0 && rateNum === bestRate;

            const urlCell =
              href.length > 0 ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={href}
                      className="text-[#00C2C2] hover:underline font-medium block min-w-0 max-w-full truncate text-left"
                    >
                      {display}
                    </a>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-lg">
                    <p className="text-xs text-white whitespace-pre-wrap break-all">{href}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <span className="text-gray-500">—</span>
              );

            return (
              <TableRow key={key}>
                <TableCell className="max-w-0 min-w-0 w-[70%] align-top py-2">
                  <div className="min-w-0 max-w-full">{urlCell}</div>
                </TableCell>
                <TableCell
                  className={`text-right text-xs tabular-nums w-[30%] whitespace-nowrap align-top py-2 ${
                    isBest ? 'bg-emerald-50 font-semibold' : ''
                  }`}
                >
                  {formatLinkClickRate(rateNum)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {showCollapse && (
        <div className="mt-2">
          <Button type="button" variant="outline" size="sm" className="text-xs h-8" onClick={() => onToggleExpand(!expanded)}>
            {expanded ? t('messagePerfShowLess') : t('messagePerfShowAll', { count: sorted.length })}
          </Button>
        </div>
      )}
    </div>
  );
}

function SimpleBreakdownTable({
  title,
  labelHeader,
  rows,
  getRowLabel,
  getRowTooltip,
  collapseThreshold,
  expanded,
  onToggleExpand,
  t,
}) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return null;
  const collapseActive =
    typeof collapseThreshold === 'number' &&
    collapseThreshold > 0 &&
    list.length > collapseThreshold &&
    typeof onToggleExpand === 'function';
  const displayed = collapseActive && !expanded ? list.slice(0, collapseThreshold) : list;
  const bests = bestRatesAmong(displayed);

  const renderLabelCell = (row, idx) => {
    const label = getRowLabel(row, idx);
    const tipRaw = getRowTooltip?.(row, idx);
    const tip = typeof tipRaw === 'string' ? tipRaw.trim() : '';
    if (tip) {
      return (
        <TableCell className="font-medium max-w-[220px]">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-left w-full max-w-full truncate cursor-help text-[#0A1A2F] font-medium hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2] rounded-sm"
              >
                {label}
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-md">
              <p className="text-xs text-white">{tip}</p>
            </TooltipContent>
          </Tooltip>
        </TableCell>
      );
    }
    return (
      <TableCell className="font-medium max-w-[220px]">
        <div className="truncate" title={typeof label === 'string' ? label : undefined}>
          {label}
        </div>
      </TableCell>
    );
  };

  return (
    <div className="mb-5 last:mb-0">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</h4>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{labelHeader}</TableHead>
            <TableHead className="text-right">{t('analyticsTableSent')}</TableHead>
            <TableHead className="text-right">{t('analyticsOpenRateCol')}</TableHead>
            <TableHead className="text-right">{t('analyticsClickRateCol')}</TableHead>
            <TableHead className="text-right">{t('analyticsReplyRateCol')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayed.map((row, idx) => {
            const rowKey = String(
              row?.key ??
                row?.signal_family ??
                row?.cta_family ??
                row?.focus ??
                idx,
            );
            const label = getRowLabel(row, idx);
            const ts = Number(row?.total_sent || 0);
            const o = Number(row?.open_rate || 0);
            const c = Number(row?.click_rate || 0);
            const r = Number(row?.reply_rate || 0);
            return (
              <TableRow key={`${rowKey}-${idx}`}>
                {renderLabelCell(row, idx)}
                <TableCell className="text-right tabular-nums">{ts}</TableCell>
                <TableCell
                  className={`text-right text-xs ${o > 0 && o === bests.bestOpen ? 'bg-emerald-50 font-semibold' : ''}`}
                >
                  {formatRate(row?.open_rate, ts)}
                </TableCell>
                <TableCell
                  className={`text-right text-xs ${c > 0 && c === bests.bestClick ? 'bg-emerald-50 font-semibold' : ''}`}
                >
                  {formatRate(row?.click_rate, ts)}
                </TableCell>
                <TableCell className={`text-right ${r > 0 && r === bests.bestReply ? 'bg-emerald-50' : ''}`}>
                  <Badge variant="secondary" className={r > 0 && r === bests.bestReply ? 'font-semibold' : ''}>
                    {formatRate(row?.reply_rate, ts)}
                  </Badge>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {collapseActive && (
        <div className="mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs h-8"
            onClick={() => onToggleExpand(!expanded)}
          >
            {expanded ? t('messagePerfShowLess') : t('messagePerfShowAll', { count: list.length })}
          </Button>
        </div>
      )}
    </div>
  );
}

function mapSendTimingChartData(rows) {
  return (rows || []).map((row) => {
    const sends = Number(row.total_sent) || 0;
    const reliable = sends >= MIN_SENDS_FOR_RATE;
    return {
      ...row,
      reply_rate: reliable ? Number(row.reply_rate) || 0 : null,
      open_rate: reliable ? Number(row.open_rate) || 0 : null,
    };
  });
}

function SendTimingDayTooltip({ active, payload, t }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  const sends = Number(p.total_sent) || 0;
  const lowVolume = sends > 0 && sends < MIN_SENDS_FOR_RATE;
  return (
    <div className="rounded-md border-0 bg-[#0A1A2F] px-3 py-2 text-xs text-white shadow-md max-w-xs">
      <p>
        {t('messagePerfSendTimingChartTooltip', {
          label: p.day_of_week,
          sends,
          open: lowVolume ? '—' : fmtPctOneDec(p.open_rate),
          click: fmtPctOneDec(p.click_rate),
          reply: lowVolume ? '—' : fmtPctOneDec(p.reply_rate),
        })}
      </p>
      {lowVolume ? (
        <p className="mt-1 text-amber-200/90 italic">
          {t('messagePerfSendTimingLowVolumeNote', { sends })}
        </p>
      ) : null}
    </div>
  );
}

function SendTimingHourTooltip({ active, payload, t }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  const hl = String(p.hour_label ?? '').trim() || `${String(p.hourKey ?? p.hour ?? '').padStart(2, '0')}:00`;
  const sends = Number(p.total_sent) || 0;
  const lowVolume = sends > 0 && sends < MIN_SENDS_FOR_RATE;
  return (
    <div className="rounded-md border-0 bg-[#0A1A2F] px-3 py-2 text-xs text-white shadow-md max-w-xs">
      <p>
        {t('messagePerfSendTimingChartTooltip', {
          label: hl,
          sends,
          open: lowVolume ? '—' : fmtPctOneDec(p.open_rate),
          click: fmtPctOneDec(p.click_rate),
          reply: lowVolume ? '—' : fmtPctOneDec(p.reply_rate),
        })}
      </p>
      {lowVolume ? (
        <p className="mt-1 text-amber-200/90 italic">
          {t('messagePerfSendTimingLowVolumeNote', { sends })}
        </p>
      ) : null}
    </div>
  );
}

function SendTimingChartsBlock({ bySendDay, bySendHour, sendTimingLoading, t }) {
  const dayData = fillDays(bySendDay);
  const hourData = fillHours(bySendHour);
  const dayChartData = mapSendTimingChartData(dayData);
  const hourChartData = mapSendTimingChartData(hourData);
  const dayPeak = pickPeakIndex(dayChartData);
  const hourPeak = pickPeakIndex(hourChartData);
  const dayEmpty = dayData.every((d) => (Number(d.total_sent) || 0) === 0);
  const hourEmpty = hourData.every((d) => (Number(d.total_sent) || 0) === 0);

  const dayBarFill = (entry, index) => {
    const sends = Number(entry.total_sent) || 0;
    if (sends < MIN_SENDS_FOR_RATE) return '#e5e7eb';
    if (index === dayPeak && dayPeak >= 0) return '#34d399';
    return '#94a3b8';
  };

  return (
    <div className="mb-5 space-y-6 min-w-0">
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          {t('messagePerfBySendDayChartTitle')}
        </h4>
        {sendTimingLoading ? (
          <div className="flex h-[220px] w-full items-center justify-center min-w-0">
            <Loader2 className="w-5 h-5 animate-spin text-[#00C2C2]" />
          </div>
        ) : dayEmpty ? (
          <p className="text-xs text-gray-500 italic">{t('messagePerfSendTimingChartEmpty')}</p>
        ) : (
          <div className="h-[220px] w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dayChartData} margin={{ top: 6, right: 28, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="dayShort" tick={{ fontSize: 10, fill: '#64748b' }} interval={0} />
                <YAxis
                  yAxisId="reply"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  domain={[0, 'auto']}
                  width={36}
                  tickFormatter={(v) => `${v}`}
                  label={{ value: t('messagePerfSendTimingYReply'), angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 10, fill: '#64748b' } }}
                />
                <YAxis
                  yAxisId="open"
                  orientation="right"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  domain={[0, 'auto']}
                  width={36}
                  tickFormatter={(v) => `${v}`}
                  label={{ value: t('messagePerfSendTimingYOpen'), angle: 90, position: 'insideRight', offset: 10, style: { fontSize: 10, fill: '#64748b' } }}
                />
                <RechartsTooltip content={<SendTimingDayTooltip t={t} />} />
                <Bar yAxisId="reply" dataKey="reply_rate" maxBarSize={40} radius={[3, 3, 0, 0]}>
                  {dayChartData.map((entry, index) => (
                    <Cell key={entry.day_of_week} fill={dayBarFill(entry, index)} />
                  ))}
                </Bar>
                <Line
                  yAxisId="open"
                  type="monotone"
                  dataKey="open_rate"
                  stroke="#93c5fd"
                  strokeWidth={1.5}
                  dot={false}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          {t('messagePerfBySendHourChartTitle')}
        </h4>
        {sendTimingLoading ? (
          <div className="flex h-[220px] w-full items-center justify-center min-w-0">
            <Loader2 className="w-5 h-5 animate-spin text-[#00C2C2]" />
          </div>
        ) : hourEmpty ? (
          <p className="text-xs text-gray-500 italic">{t('messagePerfSendTimingChartEmpty')}</p>
        ) : (
          <div className="h-[220px] w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={hourChartData} margin={{ top: 6, right: 28, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="hour_label" tick={{ fontSize: 9, fill: '#64748b' }} interval={3} />
                <YAxis
                  yAxisId="reply"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  domain={[0, 'auto']}
                  width={36}
                  label={{ value: t('messagePerfSendTimingYReply'), angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 10, fill: '#64748b' } }}
                />
                <YAxis
                  yAxisId="open"
                  orientation="right"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  domain={[0, 'auto']}
                  width={36}
                  label={{ value: t('messagePerfSendTimingYOpen'), angle: 90, position: 'insideRight', offset: 10, style: { fontSize: 10, fill: '#64748b' } }}
                />
                <RechartsTooltip content={<SendTimingHourTooltip t={t} />} />
                <Line
                  yAxisId="reply"
                  type="monotone"
                  dataKey="reply_rate"
                  stroke="#0f766e"
                  strokeWidth={2}
                  connectNulls={false}
                  dot={(props) => {
                    const { cx, cy, index, payload } = props;
                    if (cx == null || cy == null) return null;
                    const sends = Number(payload?.total_sent) || 0;
                    if (sends < MIN_SENDS_FOR_RATE) return null;
                    const isPeak = index === hourPeak && hourPeak >= 0;
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={isPeak ? 6 : 3}
                        fill={isPeak ? '#34d399' : '#0f766e'}
                        stroke={isPeak ? '#047857' : 'none'}
                        strokeWidth={isPeak ? 2 : 0}
                      />
                    );
                  }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  yAxisId="open"
                  type="monotone"
                  dataKey="open_rate"
                  stroke="#93c5fd"
                  strokeWidth={1.25}
                  connectNulls={false}
                  dot={{ r: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function buildTimezoneSelectOptions(scheduleDefaultTz, currentTz, t) {
  const primary = String(scheduleDefaultTz ?? '').trim();
  const current = String(currentTz ?? '').trim();
  const base = SEND_TIMING_TZ_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
  const seen = new Set(base.map((o) => o.value));
  let out = base.slice();
  if (primary && !seen.has(primary)) {
    out = [{ value: primary, label: `${primary} (${t('messagePerfTzSchedulingSuffix')})` }, ...out];
    seen.add(primary);
  }
  if (current && !seen.has(current)) {
    out = [{ value: current, label: `${current} (${t('messagePerfTzSchedulingSuffix')})` }, ...out];
  }
  return out;
}

function SendTimezoneSelectorRow({ selectId, value, scheduleHintTz, onChange, t }) {
  const options = buildTimezoneSelectOptions(scheduleHintTz, value, t);
  return (
    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end sm:gap-4">
      <div className="space-y-1.5 min-w-0 w-full max-w-lg">
        <Label htmlFor={selectId} className="text-sm font-medium text-[#0A1A2F]">
          {t('messagePerfSendTimeTimezoneLabel')}
        </Label>
        <select
          id={selectId}
          className="flex h-9 w-full max-w-lg cursor-pointer rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-mono text-[#0A1A2F] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#00C2C2] focus:ring-offset-0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={t('messagePerfSendTimeTimezoneLabel')}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function SourceColumn({
  columnTitle,
  data,
  linkCohort,
  linkTouches,
  expandedFocus,
  onToggleFocus,
  showFocusTable,
  t,
}) {
  const byCta = Array.isArray(data?.by_cta_family) ? data.by_cta_family : [];
  const bySigFamRaw = Array.isArray(data?.by_signal_family) ? data.by_signal_family : [];
  const bySigFam = mergeSignalFamilyRows(bySigFamRaw);
  const byFocus = Array.isArray(data?.by_focus) ? data.by_focus : [];

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 min-w-0">
      <h3 className="text-sm font-semibold text-[#0A1A2F] mb-4 border-b border-gray-100 pb-2">{columnTitle}</h3>
      <SimpleBreakdownTable
        title={t('messagePerfByCtaFamily')}
        labelHeader={t('messagePerfLabelCtaFamily')}
        rows={byCta}
        getRowLabel={(row) => ctaFamilyDisplayLabel(row?.cta_family ?? row?.key, t)}
        t={t}
      />
      <SimpleBreakdownTable
        title={t('messagePerfBySignalFamily')}
        labelHeader={t('messagePerfLabelSignalFamily')}
        rows={bySigFam}
        getRowLabel={(row) => signalFamilyDisplayLabel(row?.signal_family ?? row?.key, t)}
        getRowTooltip={(row) => signalFamilyTooltip(row?.signal_family ?? row?.key, t)}
        t={t}
      />
      <div className="mb-5 last:mb-0">
        <LinksByTouchPanel
          title={linkCohort === 'playbook' ? 'Playbook' : 'Ad-hoc'}
          cohort={linkCohort}
          touches={linkTouches}
        />
      </div>
      {showFocusTable && (
        <SimpleBreakdownTable
          title={t('messagePerfByPrimaryAngle')}
          labelHeader={t('messagePerfLabelPrimaryAngle')}
          rows={byFocus}
          getRowLabel={(row) => String(row?.focus ?? row?.key ?? '').trim() || '—'}
          collapseThreshold={SIGNAL_COLLAPSE}
          expanded={expandedFocus}
          onToggleExpand={onToggleFocus}
          t={t}
        />
      )}
    </div>
  );
}

/**
 * Top-of-tab message analytics from GET /api/email-analytics/ad-hoc?source=adhoc|playbook.
 */
export default function MessagePerformanceInsights({ tenantId, timeRange, analyticsWindow, userId }) {
  const { t } = useTranslation();
  const windowOrDays = analyticsWindow ?? timeRange;
  const applyWindow = (params) => appendAnalyticsWindow(params, coerceAnalyticsWindow(windowOrDays));
  const contextUser = useUser();
  const [loading, setLoading] = useState(true);
  const [scheduleHintTz, setScheduleHintTz] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [playbook, setPlaybook] = useState(null);
  const [adhoc, setAdhoc] = useState(null);
  const [sendTimingLoading, setSendTimingLoading] = useState(false);
  const [keywords, setKeywords] = useState(null);
  const [keywordsProductContext, setKeywordsProductContext] = useState('ALL');
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [linkByTouch, setLinkByTouch] = useState({ touches: [], cross_cohort: [] });
  const [expandedPlaybookLink, setExpandedPlaybookLink] = useState(false);
  const [expandedAdhocLink, setExpandedAdhocLink] = useState(false);
  const [expandedAdhocFocus, setExpandedAdhocFocus] = useState(false);
  /** null = awaiting main load baseline; thereafter last merged / baseline IANA tz for send-timing fetches. */
  const sendTimingTzBaselineRef = useRef(null);

  /** Full breakdown + send-timing buckets; does not re-run when the user only changes timezone. */
  useEffect(() => {
    if (!tenantId) {
      setScheduleHintTz('');
      setSelectedTimezone('');
      setPlaybook(null);
      setAdhoc(null);
      setLoading(false);
      sendTimingTzBaselineRef.current = null;
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setPlaybook(null);
      setAdhoc(null);
      setSelectedTimezone('');
      sendTimingTzBaselineRef.current = null;

      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      let scheduleTz = browserTz;
      const scheduleUserId = userId || contextUser?.id;
      if (scheduleUserId) {
        try {
          const schedRes = await fetch(
            `${API_BASE_URL}/api/settings/comms-schedule?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(scheduleUserId)}`,
            { credentials: 'include' },
          );
          if (schedRes.ok) {
            const schedJson = await schedRes.json();
            const tz = schedJson?.schedule?.timezone;
            if (typeof tz === 'string' && tz.trim()) {
              scheduleTz = tz.trim();
            }
          }
        } catch {
          /* keep browserTz */
        }
      }
      const resolved = scheduleTz || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      if (cancelled) return;
      setScheduleHintTz(resolved);
      setSelectedTimezone(resolved);

      try {
        const mkQs = (source) => {
          const p = new URLSearchParams({
            tenant_id: tenantId,
            days: String(timeRange ?? 30),
            source,
            timezone: resolved,
          });
          applyWindow(p);
          if (userId) p.append('user_id', userId);
          return p.toString();
        };
        const [resA, resP] = await Promise.all([
          fetch(`${API_BASE_URL}/api/email-analytics/ad-hoc?${mkQs('adhoc')}`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/email-analytics/ad-hoc?${mkQs('playbook')}`, { credentials: 'include' }),
        ]);
        const [jA, jP] = await Promise.all([
          resA.ok ? resA.json() : null,
          resP.ok ? resP.json() : null,
        ]);
        if (cancelled) return;
        setAdhoc(jA && typeof jA === 'object' ? jA : null);
        setPlaybook(jP && typeof jP === 'object' ? jP : null);
      } catch (e) {
        console.error('MessagePerformanceInsights fetch failed', e);
        if (!cancelled) {
          setAdhoc(null);
          setPlaybook(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, timeRange, analyticsWindow, userId, contextUser?.id]);

  useEffect(() => {
    setKeywordsProductContext('ALL');
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) {
      setKeywords(null);
      setKeywordsLoading(false);
      return;
    }

    let cancelled = false;
    setKeywordsLoading(true);

    (async () => {
      try {
        const p = new URLSearchParams({
          tenant_id: tenantId,
          days: String(timeRange ?? 30),
          product_context: keywordsProductContext,
        });
        applyWindow(p);
        if (userId) p.append('user_id', userId);
        const res = await fetch(
          `${API_BASE_URL}/api/email-analytics/keywords?${p.toString()}`,
          { credentials: 'include' },
        );
        const j = res.ok ? await res.json() : null;
        if (!cancelled) {
          setKeywords(j && typeof j === 'object' ? j : null);
        }
      } catch (e) {
        console.error('MessagePerformanceInsights keywords fetch failed', e);
        if (!cancelled) setKeywords(null);
      } finally {
        if (!cancelled) setKeywordsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId, timeRange, analyticsWindow, userId, keywordsProductContext]);

  useEffect(() => {
    if (!tenantId) {
      setLinkByTouch({ touches: [], cross_cohort: [] });
      return;
    }

    const params = new URLSearchParams({ tenant_id: tenantId, days: String(timeRange || 30) });
    applyWindow(params);
    if (keywordsProductContext && keywordsProductContext !== 'ALL') {
      params.set('target_products', keywordsProductContext);
    }

    fetch(`${API_BASE_URL}/api/email-analytics/link-by-touch?${params}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { touches: [], cross_cohort: [] }))
      .then((data) =>
        setLinkByTouch({
          touches: Array.isArray(data?.touches) ? data.touches : [],
          cross_cohort: Array.isArray(data?.cross_cohort) ? data.cross_cohort : [],
        }),
      )
      .catch(() => setLinkByTouch({ touches: [], cross_cohort: [] }));
  }, [tenantId, timeRange, analyticsWindow, keywordsProductContext]);

  /** Merge send-time arrays only; leaves breakdown tables untouched. Skips duplicate right after main load. */
  useEffect(() => {
    if (!tenantId || !selectedTimezone) return;
    if (!playbook && !adhoc) return;

    const prev = sendTimingTzBaselineRef.current;

    if (prev !== null && prev === selectedTimezone) return;

    if (prev === null) {
      sendTimingTzBaselineRef.current = selectedTimezone;
      return;
    }

    let cancelled = false;
    setSendTimingLoading(true);
    (async () => {
      try {
        const mkQs = (source) => {
          const p = new URLSearchParams({
            tenant_id: tenantId,
            days: String(timeRange ?? 30),
            source,
            timezone: selectedTimezone,
          });
          applyWindow(p);
          if (userId) p.append('user_id', userId);
          return p.toString();
        };
        const [resA, resP] = await Promise.all([
          fetch(`${API_BASE_URL}/api/email-analytics/ad-hoc?${mkQs('adhoc')}`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/email-analytics/ad-hoc?${mkQs('playbook')}`, { credentials: 'include' }),
        ]);
        const [jA, jP] = await Promise.all([
          resA.ok ? resA.json() : null,
          resP.ok ? resP.json() : null,
        ]);
        if (cancelled) return;
        setAdhoc((prevState) =>
          prevState
            ? {
                ...prevState,
                by_send_day: Array.isArray(jA?.by_send_day) ? jA.by_send_day : [],
                by_send_hour: Array.isArray(jA?.by_send_hour) ? jA.by_send_hour : [],
                timezone: jA?.timezone ?? selectedTimezone,
              }
            : prevState,
        );
        setPlaybook((prevState) =>
          prevState
            ? {
                ...prevState,
                by_send_day: Array.isArray(jP?.by_send_day) ? jP.by_send_day : [],
                by_send_hour: Array.isArray(jP?.by_send_hour) ? jP.by_send_hour : [],
                timezone: jP?.timezone ?? selectedTimezone,
              }
            : prevState,
        );
        sendTimingTzBaselineRef.current = selectedTimezone;
      } catch (e) {
        console.error('MessagePerformanceInsights send-timing fetch failed', e);
      } finally {
        if (!cancelled) setSendTimingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setSendTimingLoading(false);
    };
  }, [tenantId, timeRange, analyticsWindow, userId, selectedTimezone, playbook, adhoc]);

  useEffect(() => {
    setExpandedPlaybookLink(false);
    setExpandedAdhocLink(false);
    setExpandedAdhocFocus(false);
  }, [tenantId, timeRange, analyticsWindow, userId]);

  const showPlaybook = Number(playbook?.total_sent) > 0;
  const showAdhoc = Number(adhoc?.total_sent) > 0;

  const gridClass =
    showPlaybook && showAdhoc ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : 'grid grid-cols-1 gap-6';

  const combinedSendDay = useMemo(
    () => combineSendBuckets(playbook?.by_send_day, adhoc?.by_send_day, 'day_of_week'),
    [playbook?.by_send_day, adhoc?.by_send_day],
  );
  const combinedSendHour = useMemo(
    () => combineSendBuckets(playbook?.by_send_hour, adhoc?.by_send_hour, 'hour'),
    [playbook?.by_send_hour, adhoc?.by_send_hour],
  );
  const { subject: subjectKeywords, body: bodyKeywords } = useMemo(
    () => parseKeywordsPayload(keywords),
    [keywords],
  );

  const productContexts = useMemo(() => {
    const avail = Array.isArray(keywords?.meta?.available_product_contexts)
      ? keywords.meta.available_product_contexts
      : [];
    const specific = Array.from(
      new Set(
        avail.map((c) => String(c || '').trim().toUpperCase()).filter((c) => c && c !== 'ALL'),
      ),
    );
    return ['ALL', ...specific];
  }, [keywords]);

  useEffect(() => {
    if (!productContexts.includes(keywordsProductContext)) {
      setKeywordsProductContext('ALL');
    }
  }, [productContexts, keywordsProductContext]);

  const keywordTooltipLabels = useMemo(
    () => ({
      messages: t('analyticsTableSent'),
      open: t('analyticsOpenRateCol'),
      reply: t('analyticsReplyRateCol'),
    }),
    [t],
  );

  const showLoader = loading || (Boolean(tenantId) && !selectedTimezone);

  if (showLoader) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('messagePerformanceInsightsTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-7 h-7 animate-spin text-[#00C2C2]" />
        </CardContent>
      </Card>
    );
  }

  const hasBreakdown = showPlaybook || showAdhoc;

  const playbookLinkRows = pickLinkClickedRows(playbook);
  const adhocLinkRows = pickLinkClickedRows(adhoc);
  const playbookSourceTotalSent =
    pickFirstFiniteNumber(playbook, ['total_sent', 'totalSent', 'email_sends', 'emailSends']) ?? 0;
  const adhocSourceTotalSent =
    pickFirstFiniteNumber(adhoc, ['total_sent', 'totalSent', 'email_sends', 'emailSends']) ?? 0;
  const showFlatLinkDetails = playbookLinkRows.length > 0 || adhocLinkRows.length > 0;

  return (
    <TooltipProvider>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('messagePerformanceInsightsTitle')}</CardTitle>
          <p className="text-sm text-gray-600 mt-1 max-w-4xl">{t('messagePerformanceInsightsDescription')}</p>
          {!showPlaybook && (
            <p className="text-xs text-gray-500 mt-3">{t('messagePerfPlaybookEmptyCaption')}</p>
          )}
          {!showAdhoc && (
            <p className="text-xs text-gray-500 mt-1">{t('messagePerfAdhocEmptyCaption')}</p>
          )}
        </CardHeader>
        <CardContent>
          {hasBreakdown ? (
            <>
            <div className={gridClass}>
              {showPlaybook && (
                <SourceColumn
                  columnTitle={t('messagePerfPlaybookMessages')}
                  data={playbook}
                  linkCohort="playbook"
                  linkTouches={linkByTouch.touches}
                  showFocusTable={false}
                  t={t}
                />
              )}
              {showAdhoc && (
                <SourceColumn
                  columnTitle={t('messagePerfAdhocMessages')}
                  data={adhoc}
                  linkCohort="adhoc"
                  linkTouches={linkByTouch.touches}
                  expandedFocus={expandedAdhocFocus}
                  onToggleFocus={setExpandedAdhocFocus}
                  showFocusTable
                  t={t}
                />
              )}
            </div>
            {linkByTouch.cross_cohort.length > 0 && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                  Same link, playbook vs ad-hoc
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 text-[10px] uppercase">
                      <th className="text-left py-1">Asset</th>
                      <th className="text-right py-1">Playbook (sent)</th>
                      <th className="text-right py-1">Playbook CTR</th>
                      <th className="text-right py-1">Ad-hoc (sent)</th>
                      <th className="text-right py-1">Ad-hoc CTR</th>
                      <th className="text-right py-1">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkByTouch.cross_cohort.slice(0, 8).map((r) => {
                      const delta = Number(r.delta_pp);
                      const deltaLabel = Number.isFinite(delta)
                        ? `${delta > 0 ? '+' : ''}${delta.toFixed(1)}pp`
                        : '—';
                      return (
                        <tr key={r.url} className="border-t border-gray-100">
                          <td className="py-1 pr-2 truncate max-w-[240px]" title={r.url}>
                            <a
                              href={r.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#00A3A3] hover:underline"
                            >
                              {r.label}
                            </a>
                          </td>
                          <td className="py-1 text-right text-gray-600 tabular-nums">{r.playbook_sent}</td>
                          <td className="py-1 text-right text-gray-600 tabular-nums">
                            {Number.isFinite(Number(r.playbook_click_rate))
                              ? `${Number(r.playbook_click_rate).toFixed(1)}%`
                              : '—'}
                          </td>
                          <td className="py-1 text-right text-gray-600 tabular-nums">{r.adhoc_sent}</td>
                          <td className="py-1 text-right text-gray-600 tabular-nums">
                            {Number.isFinite(Number(r.adhoc_click_rate))
                              ? `${Number(r.adhoc_click_rate).toFixed(1)}%`
                              : '—'}
                          </td>
                          <td
                            className={`py-1 text-right font-semibold tabular-nums ${
                              delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-red-700' : 'text-gray-500'
                            }`}
                          >
                            {deltaLabel}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-[11px] text-gray-500 mt-1 italic">
                  Deltas are ad-hoc minus playbook. Positive = asset performs better in ad-hoc placement.
                </p>
              </div>
            )}
            {showFlatLinkDetails && (
              <details className="mt-6 rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                <summary className="text-xs font-medium text-gray-600 cursor-pointer select-none">
                  Show all links (flat view)
                </summary>
                <div className={`mt-4 ${gridClass}`}>
                  {showPlaybook && playbookLinkRows.length > 0 && (
                    <LinkClickedBreakdownTable
                      title={t('messagePerfByLinkClicked')}
                      rows={playbookLinkRows}
                      sourceTotalSent={playbookSourceTotalSent}
                      expanded={expandedPlaybookLink}
                      onToggleExpand={setExpandedPlaybookLink}
                      t={t}
                    />
                  )}
                  {showAdhoc && adhocLinkRows.length > 0 && (
                    <LinkClickedBreakdownTable
                      title={t('messagePerfByLinkClicked')}
                      rows={adhocLinkRows}
                      sourceTotalSent={adhocSourceTotalSent}
                      expanded={expandedAdhocLink}
                      onToggleExpand={setExpandedAdhocLink}
                      t={t}
                    />
                  )}
                </div>
              </details>
            )}
            </>
          ) : (
            <p className="text-sm text-gray-500 text-center py-6 mb-2">{t('messagePerfNoBreakdownData')}</p>
          )}
          <section className="mt-8 pt-6 border-t border-gray-200 min-w-0">
            <h3 className="text-sm font-semibold text-[#0A1A2F] mb-4">
              {t('messagePerfKeywordsSectionTitle')}
            </h3>
            <p className="text-xs text-gray-500 mb-4 max-w-3xl">{t('messagePerfKeywordsSectionHint')}</p>
            {productContexts.length > 1 && (
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Label className="text-xs text-gray-600 shrink-0">{t('messagePerfKeywordsProductLabel')}</Label>
                <ToggleGroup
                  type="single"
                  value={keywordsProductContext}
                  onValueChange={(v) => {
                    if (v && productContexts.includes(v)) setKeywordsProductContext(v);
                  }}
                  className="justify-start"
                >
                  {productContexts.map((ctx) => (
                    <ToggleGroupItem
                      key={ctx}
                      value={ctx}
                      aria-label={ctx === 'ALL' ? t('all') : ctx}
                      className="text-xs px-3 h-8 data-[state=on]:bg-[#0A1A2F] data-[state=on]:text-white"
                    >
                      {ctx === 'ALL' ? t('all') : ctx}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <MessageKeywordsWordCloud
                title={t('messagePerfKeywordsSubjectTitle')}
                items={subjectKeywords}
                emptyLabel={t('messagePerfKeywordsEmpty')}
                tooltipLabels={keywordTooltipLabels}
                loading={keywordsLoading}
              />
              <MessageKeywordsWordCloud
                title={t('messagePerfKeywordsBodyTitle')}
                items={bodyKeywords}
                emptyLabel={t('messagePerfKeywordsEmpty')}
                tooltipLabels={keywordTooltipLabels}
                loading={keywordsLoading}
              />
            </div>
          </section>
          <section className="mt-8 pt-6 border-t border-gray-200 min-w-0">
            <h3 className="text-sm font-semibold text-[#0A1A2F] mb-4">
              {t('messagePerfSendTimingSectionTitle')}
            </h3>
            <SendTimezoneSelectorRow
              selectId="msg-perf-send-tz-merged"
              value={selectedTimezone}
              scheduleHintTz={scheduleHintTz}
              onChange={setSelectedTimezone}
              t={t}
            />
            <SendTimingChartsBlock
              bySendDay={combinedSendDay}
              bySendHour={combinedSendHour}
              sendTimingLoading={sendTimingLoading}
              t={t}
            />
          </section>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
